import { useEffect, useMemo, useState } from 'react'
import { useAnimations } from '@context/AnimationsContext'
import { cx } from '@utils/cx'
import styles from './CompletionOverlay.module.css'

/**
 * CompletionOverlay - Image completion effect over puzzle board
 */

export default function CompletionOverlay ({
  imageSrc,
  alt = '',
  show = true,
  delay = 0,
  duration = 1200,
  onComplete,
  className = '',
  ...props
}) {
  const { animationsEnabled } = useAnimations()
  const [isVisible, setIsVisible] = useState(false)
  const [hasCompleted, setHasCompleted] = useState(false)

  const transitionDuration = animationsEnabled ? duration : 300

  // Handle show/hide with delay
  useEffect(() => {
    if (!show) {
      setIsVisible(false)
      setHasCompleted(false)
      return
    }

    const timer = setTimeout(() => {
      setIsVisible(true)
    }, delay)

    return () => clearTimeout(timer)
  }, [show, delay])

  // Handle completion callback
  useEffect(() => {
    if (!isVisible || hasCompleted) return

    const completionTimer = setTimeout(() => {
      setHasCompleted(true)
      if (typeof onComplete === 'function') {
        onComplete()
      }
    }, transitionDuration)

    return () => clearTimeout(completionTimer)
  }, [isVisible, hasCompleted, transitionDuration, onComplete])

  const styleVars = useMemo(() => ({
    '--completion-duration': `${transitionDuration}ms`
  }), [transitionDuration])

  if (!imageSrc) {
    return null
  }

  return (
    <div
      className={cx(
        styles.completionOverlay,
        isVisible && styles.isVisible,
        className
      )}
      style={styleVars}
      {...props}
    >
      <img
        src={imageSrc}
        alt={alt}
        className={styles.img}
        decoding='async'
        loading='eager'
      />
    </div>
  )
}
